/**
 * NavigatorX Well Data Service
 * 
 * Service for managing well data and statistics:
 * - Most favorited/navigated wells
 * - Well search trends
 * - Data source status
 * - Well overrides and corrections
 * 
 * @author Claude AI for TGE.LA
 * @version 1.0.0
 */

import {
  collection,
  collectionGroup,
  getDocs,
  getDoc,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  increment
} from 'firebase/firestore';
import { navDb } from '../firebase/client';
import { NAV_USERS_COLLECTION } from './navigatorXService';

// ============================================================================
// TYPES
// ============================================================================

export interface WellStats {
  wellId: string;
  wellName: string;
  operator?: string;
  county?: string;
  state?: string;
  latitude?: number;
  longitude?: number;
  favoriteCount: number;
  navigationCount: number;
  lastAccessed?: Timestamp;
}

export interface WellOverride {
  id: string;
  wellId: string;
  type: 'correction' | 'blacklist' | 'custom';
  field?: string;
  originalValue?: string;
  correctedValue?: string;
  reason: string;
  createdAt: Timestamp;
  createdBy: string;
  approved: boolean;
  approvedAt?: Timestamp;
  approvedBy?: string;
}

export interface DataSourceStatus {
  source: string;
  lastSync: Timestamp;
  status: 'healthy' | 'degraded' | 'error';
  recordCount: number;
  errorCount: number;
  lastError?: string;
  syncDuration?: number;
}

export interface SearchTrend {
  term: string;
  count: number;
  lastSearched: Timestamp;
}

export interface WellDataStats {
  totalFavorites: number;
  totalNavigations: number;
  uniqueWellsFavorited: number;
  uniqueWellsNavigated: number;
  topFavoritedWells: WellStats[];
  topNavigatedWells: WellStats[];
  recentSearches: SearchTrend[];
  dataSourceStatus: DataSourceStatus[];
}

// ============================================================================
// CONSTANTS
// ============================================================================

const WELL_STATS_COLLECTION = 'nav_well_stats';
const WELL_OVERRIDES_COLLECTION = 'nav_well_overrides';
const SEARCH_TRENDS_COLLECTION = 'nav_search_trends';
const DATA_SOURCE_STATUS_COLLECTION = 'nav_data_sources';

// ============================================================================
// WELL STATISTICS
// ============================================================================

/**
 * Get aggregated well statistics
 */
export async function getWellDataStats(): Promise<WellDataStats> {
  // Get all favorites across users
  const favoritesSnapshot = await getDocs(
    collectionGroup(navDb, 'favorites')
  );
  
  // Get all history across users
  const historySnapshot = await getDocs(
    collectionGroup(navDb, 'well_history')
  );

  // Aggregate favorites by well
  const favoritesByWell = new Map<string, { count: number; wellData: any }>();
  favoritesSnapshot.docs.forEach(doc => {
    const data = doc.data();
    const wellId = doc.id;
    const existing = favoritesByWell.get(wellId);
    if (existing) {
      existing.count++;
    } else {
      favoritesByWell.set(wellId, { count: 1, wellData: data });
    }
  });

  // Aggregate navigations by well
  const navigationsByWell = new Map<string, { count: number; wellData: any }>();
  historySnapshot.docs.forEach(doc => {
    const data = doc.data();
    const wellId = doc.id;
    const existing = navigationsByWell.get(wellId);
    if (existing) {
      existing.count++;
    } else {
      navigationsByWell.set(wellId, { count: 1, wellData: data });
    }
  });

  // Sort and get top wells
  const topFavoritedWells: WellStats[] = Array.from(favoritesByWell.entries())
    .map(([wellId, { count, wellData }]) => ({
      wellId,
      wellName: wellData.wellName || wellData.name || wellId,
      operator: wellData.operator,
      county: wellData.county,
      state: wellData.state,
      latitude: wellData.latitude,
      longitude: wellData.longitude,
      favoriteCount: count,
      navigationCount: navigationsByWell.get(wellId)?.count || 0
    }))
    .sort((a, b) => b.favoriteCount - a.favoriteCount)
    .slice(0, 20);

  const topNavigatedWells: WellStats[] = Array.from(navigationsByWell.entries())
    .map(([wellId, { count, wellData }]) => ({
      wellId,
      wellName: wellData.wellName || wellData.name || wellId,
      operator: wellData.operator,
      county: wellData.county,
      state: wellData.state,
      latitude: wellData.latitude,
      longitude: wellData.longitude,
      favoriteCount: favoritesByWell.get(wellId)?.count || 0,
      navigationCount: count
    }))
    .sort((a, b) => b.navigationCount - a.navigationCount)
    .slice(0, 20);

  // Get search trends
  const trendsSnapshot = await getDocs(
    query(
      collection(navDb, SEARCH_TRENDS_COLLECTION),
      orderBy('count', 'desc'),
      limit(20)
    )
  );
  const recentSearches = trendsSnapshot.docs.map(doc => ({
    term: doc.id,
    ...doc.data()
  })) as SearchTrend[];

  // Get data source status
  const sourcesSnapshot = await getDocs(collection(navDb, DATA_SOURCE_STATUS_COLLECTION));
  const dataSourceStatus = sourcesSnapshot.docs.map(doc => ({
    source: doc.id,
    ...doc.data()
  })) as DataSourceStatus[];

  return {
    totalFavorites: favoritesSnapshot.size,
    totalNavigations: historySnapshot.size,
    uniqueWellsFavorited: favoritesByWell.size,
    uniqueWellsNavigated: navigationsByWell.size,
    topFavoritedWells,
    topNavigatedWells,
    recentSearches,
    dataSourceStatus
  };
}

/**
 * Get most favorited wells
 */
export async function getMostFavoritedWells(limitCount: number = 20): Promise<WellStats[]> {
  const stats = await getWellDataStats();
  return stats.topFavoritedWells.slice(0, limitCount);
}

/**
 * Get most navigated wells
 */
export async function getMostNavigatedWells(limitCount: number = 20): Promise<WellStats[]> {
  const stats = await getWellDataStats();
  return stats.topNavigatedWells.slice(0, limitCount);
}

// ============================================================================
// WELL OVERRIDES
// ============================================================================

/**
 * Create a well override/correction
 */
export async function createWellOverride(
  override: Omit<WellOverride, 'id' | 'createdAt' | 'approved'>
): Promise<string> {
  const id = `${override.wellId}_${override.type}_${Date.now()}`;
  await setDoc(doc(navDb, WELL_OVERRIDES_COLLECTION, id), {
    ...override,
    createdAt: Timestamp.now(),
    approved: false
  });
  return id;
}

/**
 * Get all well overrides
 */
export async function getWellOverrides(options: {
  type?: WellOverride['type'];
  pendingOnly?: boolean;
} = {}): Promise<WellOverride[]> {
  let q = query(
    collection(navDb, WELL_OVERRIDES_COLLECTION),
    orderBy('createdAt', 'desc')
  );

  if (options.type) {
    q = query(q, where('type', '==', options.type));
  }

  if (options.pendingOnly) {
    q = query(q, where('approved', '==', false));
  }

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as WellOverride[];
}

/**
 * Approve a well override
 */
export async function approveWellOverride(
  overrideId: string,
  adminId: string
): Promise<void> {
  await updateDoc(doc(navDb, WELL_OVERRIDES_COLLECTION, overrideId), {
    approved: true,
    approvedAt: Timestamp.now(),
    approvedBy: adminId
  });
}

/**
 * Delete a well override
 */
export async function deleteWellOverride(overrideId: string): Promise<void> {
  await deleteDoc(doc(navDb, WELL_OVERRIDES_COLLECTION, overrideId));
}

/**
 * Blacklist a well
 */
export async function blacklistWell(
  wellId: string,
  reason: string,
  adminId: string
): Promise<string> {
  return createWellOverride({
    wellId,
    type: 'blacklist',
    reason,
    createdBy: adminId
  });
}

/**
 * Create a custom well
 */
export async function createCustomWell(
  wellData: {
    wellId: string;
    wellName: string;
    operator?: string;
    latitude: number;
    longitude: number;
    county?: string;
    state?: string;
  },
  adminId: string
): Promise<string> {
  return createWellOverride({
    wellId: wellData.wellId,
    type: 'custom',
    correctedValue: JSON.stringify(wellData),
    reason: 'Custom well added by admin',
    createdBy: adminId
  });
}

// ============================================================================
// DATA SOURCE MANAGEMENT
// ============================================================================

/**
 * Update data source status
 */
export async function updateDataSourceStatus(
  source: string,
  status: Partial<Omit<DataSourceStatus, 'source'>>
): Promise<void> {
  await setDoc(doc(navDb, DATA_SOURCE_STATUS_COLLECTION, source), {
    source,
    ...status,
    lastSync: Timestamp.now()
  }, { merge: true });
}

/**
 * Get data source status
 */
export async function getDataSourceStatus(source: string): Promise<DataSourceStatus | null> {
  const docSnap = await getDoc(doc(navDb, DATA_SOURCE_STATUS_COLLECTION, source));
  if (!docSnap.exists()) return null;
  return { source: docSnap.id, ...docSnap.data() } as DataSourceStatus;
}

/**
 * Get all data sources
 */
export async function getAllDataSources(): Promise<DataSourceStatus[]> {
  const snapshot = await getDocs(collection(navDb, DATA_SOURCE_STATUS_COLLECTION));
  return snapshot.docs.map(doc => ({
    source: doc.id,
    ...doc.data()
  })) as DataSourceStatus[];
}

// ============================================================================
// SEARCH TRENDS
// ============================================================================

/**
 * Record a search term
 */
export async function recordSearchTerm(term: string): Promise<void> {
  const normalizedTerm = term.toLowerCase().trim();
  const docRef = doc(navDb, SEARCH_TRENDS_COLLECTION, normalizedTerm);
  
  await setDoc(docRef, {
    term: normalizedTerm,
    count: increment(1),
    lastSearched: Timestamp.now()
  }, { merge: true });
}

/**
 * Get top search terms
 */
export async function getTopSearchTerms(limitCount: number = 20): Promise<SearchTrend[]> {
  const snapshot = await getDocs(
    query(
      collection(navDb, SEARCH_TRENDS_COLLECTION),
      orderBy('count', 'desc'),
      limit(limitCount)
    )
  );
  
  return snapshot.docs.map(doc => ({
    term: doc.id,
    ...doc.data()
  })) as SearchTrend[];
}

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

const navigatorXWellService = {
  // Statistics
  getWellDataStats,
  getMostFavoritedWells,
  getMostNavigatedWells,
  
  // Overrides
  createWellOverride,
  getWellOverrides,
  approveWellOverride,
  deleteWellOverride,
  blacklistWell,
  createCustomWell,
  
  // Data Sources
  updateDataSourceStatus,
  getDataSourceStatus,
  getAllDataSources,
  
  // Search Trends
  recordSearchTerm,
  getTopSearchTerms
};

export default navigatorXWellService;
